import { api } from "@/integrations/clients/carAuction/carAuctionClient";
import {
  BaseSupportCase,
  SupportCaseDetails,
  UpdateSupportCaseRequest,
  AddCommentRequest,
  SupportCaseFilters,
  SupportComment,
  AddSupportCaseDocumentRequest,
  RemoveSupportCaseDocumentRequest,
} from "@/types/support";
import { FileObject } from "@/types/shared/file";
import {
  ApiCase,
  mapApiCaseToDetails,
  mapFrontendStatusToApi,
} from "./supportApiMappers";

interface ApiListResponse {
  success: boolean;
  data?: ApiCase[];
  count?: number;
  total?: number;
}

interface ApiItemResponse<T> {
  success: boolean;
  data?: T;
}

const ADMIN_STATUS_TO_API: Record<string, string> = {
  not_started: "open",
  in_progress: "ongoing",
  resolved: "closed",
  closed: "closed",
};

const ADMIN_PRIORITY_TO_API: Record<string, string> = {
  low: "low",
  medium: "medium",
  high: "high",
  urgent: "high",
};

export class SupportCaseManagementService {
  static async getAllSupportCases(
    filters?: SupportCaseFilters & {
      page?: number;
      limit?: number;
      search?: string;
    },
  ): Promise<{
    cases: BaseSupportCase[];
    total: number;
    page: number;
    totalPages: number;
  }> {
    const limit = filters?.limit || 10;
    const page = filters?.page || 1;
    const offset = (page - 1) * limit;

    const response = await api.get<ApiListResponse>("/admin/support/cases", {
      params: {
        limit,
        offset,
        search: filters?.search,
        type: filters?.type,
        status: filters?.status
          ? ADMIN_STATUS_TO_API[filters.status] ?? filters.status
          : undefined,
        priority: filters?.priority
          ? ADMIN_PRIORITY_TO_API[filters.priority] ?? filters.priority
          : undefined,
      },
    });

    const items = response.data || [];
    const total = response.total ?? items.length;
    const cases = items.map((item) => {
      const details = mapApiCaseToDetails(item);
      return {
        id: details.id,
        customerName: details.customerName,
        customerEmail: details.customerEmail,
        type: details.type,
        category: details.category,
        status: details.status,
        priority: details.priority,
        created: details.created,
        lastUpdated: details.lastUpdated,
        regNr: details.regNr,
        caseRef: details.caseRef,
        vehicleRegNr: details.vehicleRegNr,
        hasUnread: details.hasUnread,
        description: details.description,
      };
    });

    return {
      cases,
      total,
      page,
      totalPages: Math.ceil(total / limit) || 1,
    };
  }

  static async getSupportCase(id: string): Promise<SupportCaseDetails> {
    const response = await api.get<ApiItemResponse<ApiCase>>(
      `/admin/support/cases/${id}`,
    );
    if (!response.success || !response.data) {
      throw new Error("Support case not found");
    }
    return mapApiCaseToDetails(response.data);
  }

  static async updateSupportCase(
    caseId: string,
    updates: Partial<UpdateSupportCaseRequest>,
  ): Promise<SupportCaseDetails> {
    const payload: Record<string, string> = {};
    if (updates.status) {
      payload.status = ADMIN_STATUS_TO_API[updates.status] ?? updates.status;
    }
    if (updates.priority) {
      payload.priority =
        ADMIN_PRIORITY_TO_API[updates.priority] ?? updates.priority;
    }
    if (updates.category) payload.category = updates.category;
    if (updates.description) payload.description = updates.description;

    const response = await api.put<ApiItemResponse<ApiCase>>(
      `/admin/support/cases/${caseId}`,
      payload,
    );
    if (!response.success || !response.data) {
      throw new Error("Failed to update support case");
    }
    return mapApiCaseToDetails(response.data);
  }

  static async addComment(request: AddCommentRequest): Promise<SupportComment> {
    const response = await api.post<
      ApiItemResponse<{
        id: string;
        content: string;
        isInternal?: boolean;
        author?: {
          id?: string;
          username?: string;
          firstName?: string;
          lastName?: string;
        };
        createdAt: string;
      }>
    >(`/admin/support/cases/${request.supportCaseId}/comments`, {
      content: request.content,
      isInternal: request.isInternal ?? false,
      notifyColleagues: request.notifyColleagues,
    });

    if (!response.success || !response.data) {
      throw new Error("Failed to add comment");
    }

    const author = response.data.author;
    const authorName =
      [author?.firstName, author?.lastName].filter(Boolean).join(" ") ||
      author?.username ||
      "Admin";

    return {
      id: response.data.id,
      supportCaseId: request.supportCaseId,
      authorId: author?.id || "",
      authorName,
      content: response.data.content,
      createdAt: response.data.createdAt,
      isInternal: !!response.data.isInternal,
    };
  }

  static async addSupportCaseDocument(
    request: AddSupportCaseDocumentRequest,
  ): Promise<FileObject> {
    const formData = new FormData();
    formData.append("file", request.document);
    formData.append("name", request.document.name);

    const response = await api.post<
      ApiItemResponse<{
        id?: string;
        url?: string;
        name?: string;
        originalName?: string;
        size?: number;
        mimeType?: string;
      }>
    >(`/admin/support/cases/${request.supportCaseId}/documents`, formData);

    if (!response.success || !response.data) {
      throw new Error("Failed to upload document");
    }

    return {
      id: response.data.id || `file_${Date.now()}`,
      url: response.data.url || "",
      name: response.data.name || response.data.originalName || "document",
      type: response.data.mimeType || request.document.type,
      size: response.data.size || request.document.size,
    };
  }

  static async removeSupportCaseDocument(
    request: RemoveSupportCaseDocumentRequest,
  ): Promise<void> {
    await api.delete(
      `/support/cases/${request.supportCaseId}/documents/${request.documentId}`,
    );
  }
}

export { mapFrontendStatusToApi };
